import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { THIRD_PARTY_PUBLIC } from '@/lib/third-party'
import { THIRD_PARTY_GUIDE_ENTRY } from '@/components/third-party-marketing/public-copy'

export const Route = createFileRoute('/_site/guides/')({
  component: GuidesIndexPage,
  head: () => ({
    meta: [
      { title: 'Guides — Uptrack Uptime Monitoring' },
      { name: 'description', content: 'Learn how to monitor websites, APIs, and cron jobs. Step-by-step guides for Slack integration, status pages, API monitoring, and more.' },
      { property: 'og:title', content: 'Guides — Uptrack' },
      { property: 'og:description', content: 'Learn how to monitor websites, APIs, and cron jobs. Step-by-step guides for Slack integration, status pages, API monitoring, and more.' },
      { property: 'og:image', content: 'https://uptrack.app/og/guides.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/guides' },
    ],
  }),
})

const allGuides = [
  {
    slug: '/guides/switch-from-uptimerobot',
    title: 'Switch from UptimeRobot',
    description: 'Move your monitors in minutes: export to CSV, drag it into Uptrack, and every monitor comes across — no manual re-entry.',
    category: 'Getting Started',
  },
  {
    slug: '/guides/switch-from-pingdom',
    title: 'Switch from Pingdom',
    description: 'Export your checks to CSV and bulk-import them into Uptrack — HTTP, ping, TCP, and keyword checks all map over. No manual re-entry.',
    category: 'Getting Started',
  },
  {
    slug: '/guides/monitoring-as-code',
    title: 'Monitoring as Code (CLI)',
    description: 'Keep your monitors in a version-controlled YAML file and sync them from CI with the uptrack CLI — GitOps for monitoring.',
    category: 'Integrations',
  },
  {
    slug: '/guides/api-monitoring',
    title: 'Monitor API Endpoints',
    description: 'Validate status codes, response bodies (JSONPath), headers, and response time. With code examples.',
    category: 'Monitoring',
  },
  {
    slug: '/guides/browser-checks',
    title: 'Browser Checks & Core Web Vitals',
    description: 'Why 200 OK isn\'t enough. Capture real-browser screenshots and track LCP, CLS, FCP, and TTFB on a schedule.',
    category: 'Monitoring',
  },
  {
    slug: '/guides/ssl-monitoring',
    title: 'Check SSL Certificate Expiration',
    description: 'Commands to check certificate expiry (openssl, curl, Python, Node), why renewals fail silently, and how to get warned in time.',
    category: 'Monitoring',
  },
  {
    slug: '/guides/status-pages',
    title: 'Public Status Pages',
    description: 'Build trust with customers. Show real-time uptime, incident history, and maintenance schedules.',
    category: 'Status Pages',
  },
  {
    slug: '/guides/mcp',
    title: 'Uptrack MCP Server',
    description: 'Connect Claude to Uptrack. List monitors, create checks and acknowledge incidents in plain language.',
    category: 'Integrations',
  },
  {
    slug: '/guides/sms-and-call-alerts-faq',
    title: 'SMS and Phone Call Alerts',
    description: 'Which countries we can reach by SMS and by call, what each plan includes, and what it costs.',
    category: 'Integrations',
  },
  {
    slug: '/guides/slack-commands',
    title: 'Slack Slash Commands',
    description: 'Check uptime status from Slack with /uptrack status. See which sites are up or down.',
    category: 'Integrations',
  },
  {
    slug: '/guides/zapier',
    title: 'Connect to Zapier',
    description: 'Send Uptrack alerts to 6,000+ apps via webhooks — Google Sheets, PagerDuty, Notion, and more.',
    category: 'Integrations',
  },
  {
    slug: '/guides/monitor-saas-landing-page',
    title: 'Monitor Your SaaS Landing Page',
    description: 'Why landing page downtime costs signups, and how to catch it before your users do.',
    category: 'Monitoring',
  },
  {
    slug: '/guides/monitor-side-project',
    title: 'Monitor Your Side Project',
    description: 'Complete setup guide for monitoring side projects. Pro includes 50 monitors at 30s.',
    category: 'Getting Started',
  },
  {
    slug: '/guides/webhook-payloads',
    title: 'Webhook Payload Reference',
    description: 'Exact JSON payloads for monitor.down, monitor.up, and ssl.expiring events. With code examples.',
    category: 'Integrations',
  },
]

/** The guides to list. The third party guide is listed only when THIRD_PARTY_PUBLIC is true. */
export function visibleGuides() {
  if (!THIRD_PARTY_PUBLIC) return allGuides
  const at = allGuides.findIndex((g) => g.slug === '/guides/api-monitoring')
  return [...allGuides.slice(0, at), THIRD_PARTY_GUIDE_ENTRY, ...allGuides.slice(at)]
}

function GuidesIndexPage() {
  const guides = visibleGuides()
  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Guides</h1>
      <p className="mt-4 max-w-2xl text-lead text-body">
        Step-by-step tutorials for monitoring, alerting, integrations, and status pages.
      </p>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {guides.map((guide) => (
          <Link
            key={guide.slug}
            to={guide.slug}
            className="group flex flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
          >
            <p className="text-xs text-muted-foreground">{guide.category}</p>
            <h2 className="mt-2 text-[17px] font-semibold leading-snug text-foreground group-hover:text-primary">{guide.title}</h2>
            <p className="mt-2 flex-1 text-[15px] leading-relaxed text-body">{guide.description}</p>
            <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
              Read guide <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}
